import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, X, Scale, MessageSquare, CheckCircle2, Mail, Phone, MapPin } from 'lucide-react';
import { useWBL } from '../../context/WBLContext';

export function PrivacyModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;
  return (
    <AnimatePresence>
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 text-left">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#020d1a]/85 backdrop-blur-md"
          />
          
          {/* Modal Box */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="relative w-full max-w-3xl bg-[#091526] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] z-10"
          >
            {/* Header */}
            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-black/20 shrink-0">
              <div className="flex items-center space-x-2.5">
                <ShieldCheck className="w-5 h-5 text-[#b9e612]" />
                <h3 className="text-sm font-black text-white uppercase tracking-widest">
                  WBL PORTAL: PRIVACY POLICY
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                title="Close Legal Document"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Body */}
            <div className="p-6 md:p-8 overflow-y-auto text-slate-300 text-xs md:text-sm leading-relaxed space-y-6 antialiased text-left">
              <div className="flex items-center justify-between border-b border-slate-700/20 pb-4 mb-2">
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#b9e612]">Official Disclosure Protocol</span>
                <span className="text-[10px] font-mono text-slate-400">Effective Date: May 1, 2026</span>
              </div>
              <p className="font-semibold text-slate-200">
                The WBL Portal recognizes that professional data is personal. This policy outlines how we collect, protect, and share information within our regional Work-Based Learning (WBL) ecosystem.
              </p>
              
              {/* 1. Our Role */}
              <div className="space-y-3 pt-2">
                <h4 className="text-white font-bold uppercase tracking-widest text-[11px] border-l-2 border-[#0094c8] pl-3">1. Information Collection Architecture</h4>
                <p>We log data when users (students, educators, parents, employer partners) interface with the portal. This includes:</p>
                <ul className="list-disc pl-5 space-y-2 text-slate-400 marker:text-slate-600">
                  <li><span className="text-slate-300 font-medium">Account Identifiers:</span> Names, emails, regional affiliations (schools/districts), and role-based permissions.</li>
                  <li><span className="text-slate-300 font-medium">Professional Logs:</span> Resumes, skill certifications, hours tracked, application statuses, and evaluation rubrics.</li>
                  <li><span className="text-slate-300 font-medium">Statutory Consents:</span> Digital signatures and timestamps for agreements like the Master Training Agreement or FERPA releases.</li>
                  <li><span className="text-slate-300 font-medium">Telemetry Logs:</span> Non-personally identifiable diagnostic data regarding portal interaction, browser types, and timestamp metrics.</li>
                </ul>
              </div>

              {/* 2. Utilization Protocol */}
              <div className="space-y-3 pt-2">
                <h4 className="text-white font-bold uppercase tracking-widest text-[11px] border-l-2 border-[#0094c8] pl-3">2. Utilization Protocol</h4>
                <p>Your data is strictly utilized to facilitate the WBL pathway mechanics. We use it to:</p>
                <ul className="list-disc pl-5 space-y-2 text-slate-400 marker:text-slate-600">
                  <li>Match student profiles against active regional employer opportunities.</li>
                  <li>Provide real-time analytics to educational administrators regarding district-level WBL participation.</li>
                  <li>Verify compliance with state and federal labor/education standards.</li>
                  <li>Send essential routing alerts (e.g., application updates, pending signatures).</li>
                </ul>
              </div>

              {/* 3. Distribution Safeguards */}
              <div className="space-y-3 pt-2">
                <h4 className="text-white font-bold uppercase tracking-widest text-[11px] border-l-2 border-[#b9e612] pl-3">3. Distribution Safeguards & FERPA Alignment</h4>
                <p>
                  We are bound by the Family Educational Rights and Privacy Act (FERPA). Student data is <span className="text-slate-200 font-bold">never sold, monetized, or brokered</span>. 
                </p>
                <p>
                  Data is only exposed to authorized nodes:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-slate-400 marker:text-slate-600">
                  <li><span className="text-slate-300 font-medium">Employers:</span> View resumes and profiles only when a student explicitly initiates an application.</li>
                  <li><span className="text-slate-300 font-medium">Educators/Parents:</span> View their specific assigned student cohorts in accordance with established RBAC (Role-Based Access Control) policies.</li>
                </ul>
              </div>

              {/* 4. Security Infrastructure */}
              <div className="space-y-3 pt-2">
                <h4 className="text-white font-bold uppercase tracking-widest text-[11px] border-l-2 border-[#0094c8] pl-3">4. Security Infrastructure</h4>
                <p>
                  The portal operates within a fortified perimeter utilizing 256-bit AES encryption at rest and TLS 1.3 in transit. Authentication is managed via decentralized identity providers ensuring password hashes never cross our core databases.
                </p>
              </div>

              <div className="pt-6 border-t border-slate-800 text-center text-xs text-slate-500 font-mono">
                [END OF POLICY LOG] - If you require clarification on these parameters, please ping our support desk.
              </div>
            </div>
          </motion.div>
        </div>
    </AnimatePresence>
  );
}

export function TermsModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;
  return (
    <AnimatePresence>
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 text-left">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#020d1a]/85 backdrop-blur-md"
          />
          
          {/* Modal Box */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="relative w-full max-w-3xl bg-[#091526] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] z-10"
          >
            {/* Header */}
            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-black/20 shrink-0">
              <div className="flex items-center space-x-2.5">
                <Scale className="w-5 h-5 text-[#b9e612]" />
                <h3 className="text-sm font-black text-white uppercase tracking-widest">
                  WBL PORTAL: TERMS OF SERVICE
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                title="Close Legal Document"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Body */}
            <div className="p-6 md:p-8 overflow-y-auto text-slate-300 text-xs md:text-sm leading-relaxed space-y-6 antialiased text-left">
              <div className="flex items-center justify-between border-b border-slate-700/20 pb-4 mb-2">
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#b9e612]">Operational Directives</span>
                <span className="text-[10px] font-mono text-slate-400">Effective Date: May 1, 2026</span>
              </div>
              <p className="font-semibold text-slate-200">
                Welcome to the Work-Based Learning (WBL) Operations Portal. By accessing or provisioning an account within this system, you agree to comply with the following operational directives.
              </p>
              
              {/* 1. Account Integrity */}
              <div className="space-y-3 pt-2">
                <h4 className="text-white font-bold uppercase tracking-widest text-[11px] border-l-2 border-[#0094c8] pl-3">1. Account Integrity & Authorization</h4>
                <p>
                  Credentials deployed to users are strictly non-transferable. You are responsible for all activity logged under your identity hash. Administrators (Superadmins and Region Owners) possess the authority to terminate or suspend node access if abnormal or malicious activity is detected.
                </p>
              </div>

              {/* 2. Platform Usage */}
              <div className="space-y-3 pt-2">
                <h4 className="text-white font-bold uppercase tracking-widest text-[11px] border-l-2 border-[#0094c8] pl-3">2. Permitted Operations</h4>
                <p>The platform is engineered exclusively to facilitate educational and professional placement mechanics. Users agree not to:</p>
                <ul className="list-disc pl-5 space-y-2 text-slate-400 marker:text-slate-600">
                  <li>Scrape, datamine, or run automated extraction routines against the portal directories.</li>
                  <li>Submit falsified data, including counterfeit resumes, forged digital signatures, or invalid corporate entities.</li>
                  <li>Attempt to bypass Role-Based Access Control (RBAC) boundaries to view unassigned regional data.</li>
                </ul>
              </div>

              {/* 3. Employer Obligations */}
              <div className="space-y-3 pt-2">
                <h4 className="text-white font-bold uppercase tracking-widest text-[11px] border-l-2 border-[#b9e612] pl-3">3. Corporate Partner Obligations</h4>
                <p>
                  Employers utilizing the portal agree to adhere to all state and federal labor statutes regarding student interns and apprentices. Placement listings must represent genuine, active opportunities. 
                </p>
              </div>

              {/* 4. Limitation of Liability */}
              <div className="space-y-3 pt-2">
                <h4 className="text-white font-bold uppercase tracking-widest text-[11px] border-l-2 border-[#0094c8] pl-3">4. Limitation of Liability & Vendor Protection (LosoNova LLC)</h4>
                <p>
                  The platform is provided "as-is" without warranty. We do not guarantee uninterrupted server uptime or the ultimate success of employment matching. The portal operators are not liable for disputes arising between students, educators, and corporate partners outside the digital boundaries of the application.
                </p>
                
                {/* 4.2 Vendor Protection & Reliance on User Attestation */}
                <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-800 space-y-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-wider text-[#b9e612]">
                      4.2 Vendor Protection & Reliance on Affirmative Attestation
                    </span>
                    <span className="text-[9px] font-mono text-slate-500">LosoNova LLC &bull; UETA / E-SIGN</span>
                  </div>
                  <p className="text-xs text-slate-300">
                    <strong className="text-white">Accuracy Disclaimer:</strong> LosoNova LLC provides electronic delivery software and relies strictly on affirmative certifications and legal attestations made by individual user signers (including students and legal guardians) under E-SIGN and UETA statutes.
                  </p>
                  <p className="text-xs text-slate-300">
                    <strong className="text-white">No Independent Audit Obligation:</strong> The platform does not independently audit familial relationships. Liability for fraudulent or incorrect information submitted by students rests solely on the individual making the false representation, holding school districts and LosoNova LLC harmless.
                  </p>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-800 text-center text-xs text-slate-500 font-mono">
                [END OF TERMS FILE]
              </div>
            </div>
          </motion.div>
        </div>
    </AnimatePresence>
  );
}

export function ContactModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { globalBranding } = useWBL();
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [inquirySubmitted, setInquirySubmitted] = useState(false);
  const [inquiryError, setInquiryError] = useState('');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 text-left">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#020d1a]/85 backdrop-blur-md"
          />
          
          {/* Modal Box */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="relative w-full max-w-4xl bg-[#091526] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] z-10"
          >
            {/* Header */}
            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-black/20 shrink-0">
              <div className="flex items-center space-x-2.5">
                <MessageSquare className="w-5 h-5 text-[#b9e612]" />
                <h3 className="text-sm font-black text-white uppercase tracking-widest">
                  Contact Us Support & Inquiries
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                title="Close Form"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Body */}
            <div className="p-6 md:p-8 overflow-y-auto flex-1">
              {inquirySubmitted ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center text-center py-12 space-y-4"
                >
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-2">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h4 className="text-lg font-black text-white uppercase tracking-wider">Inquiry Submitted Successfully</h4>
                  <p className="text-sm text-slate-300 max-w-md leading-relaxed">
                    Thank you for reaching out, <span className="text-[#b9e612] font-semibold">{contactName}</span>! Your message has been received and routed to our regional administration desk in Spartanburg County.
                  </p>
                  <p className="text-xs text-slate-400">
                    A copy of your inquiry details has been logged in our secure processing logs.
                  </p>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{ backgroundColor: globalBranding?.palette?.lightMain || '#0094c8' }}
                    className="mt-6 px-6 py-2.5 text-white rounded-xl font-black uppercase text-[11px] tracking-widest transition-all hover:scale-[1.02] active:scale-98 cursor-pointer shadow-md border-0"
                  >
                    Close Window
                  </button>
                </motion.div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                  {/* Left details pane */}
                  <div className="md:col-span-2 space-y-6 text-left border-b md:border-b-0 md:border-r border-slate-800 pb-6 md:pb-0 md:pr-8">
                    <div>
                      <span className="text-[10px] uppercase font-bold tracking-widest text-[#b9e612]">WBL Support Desk</span>
                      <h4 className="text-xl font-black text-white mt-1 mb-3">Get in Touch</h4>
                      <p className="text-slate-300 text-xs md:text-sm leading-relaxed font-medium">
                        Have questions about Work-Based Learning in Spartanburg County? Our team is here to help students, employers, and advisors.
                      </p>
                    </div>
                    <div className="space-y-4 pt-4 border-t border-slate-800/60">
                      <div className="flex items-start space-x-3 text-xs">
                        <Mail className="w-4 h-4 text-[#0094c8] shrink-0 mt-0.5" />
                        <div>
                          <div className="text-slate-400 font-bold uppercase tracking-wider text-[10px] mb-0.5">Email Support</div>
                          <div className="text-white">support@spartanburgwbl.edu</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 text-xs">
                        <Phone className="w-4 h-4 text-[#0094c8] shrink-0 mt-0.5" />
                        <div>
                          <div className="text-slate-400 font-bold uppercase tracking-wider text-[10px] mb-0.5">Direct Line</div>
                          <div className="text-white">(864) 555-0198</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 text-xs">
                        <MapPin className="w-4 h-4 text-[#0094c8] shrink-0 mt-0.5" />
                        <div>
                          <div className="text-slate-400 font-bold uppercase tracking-wider text-[10px] mb-0.5">Regional Office</div>
                          <div className="text-white">100 Education Way<br/>Spartanburg, SC 29306</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right form pane */}
                  <div className="md:col-span-3">
                    <form 
                      className="space-y-4 text-left"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!contactName || !contactEmail || !contactSubject || !contactMessage) {
                          setInquiryError('Please complete all required fields prior to submission.');
                          return;
                        }
                        setInquiryError('');
                        setInquirySubmitted(true);
                      }}
                    >
                      {inquiryError && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-xl text-xs font-medium">
                          {inquiryError}
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Full Name *</label>
                          <input 
                            type="text" 
                            className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] transition-all"
                            placeholder="John Doe"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Email Address *</label>
                          <input 
                            type="email" 
                            className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] transition-all"
                            placeholder="john@example.com"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Inquiry Subject *</label>
                        <select 
                          className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] transition-all appearance-none"
                          value={contactSubject}
                          onChange={(e) => setContactSubject(e.target.value)}
                        >
                          <option value="" disabled className="text-slate-500">Select a topic...</option>
                          <option value="student">Student Account / Application Support</option>
                          <option value="employer">Employer / Placement Partner Support</option>
                          <option value="educator">Educator / Advisor Support</option>
                          <option value="technical">Technical Issue / Bug Report</option>
                          <option value="other">Other Inquiry</option>
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Message Detail *</label>
                        <textarea 
                          className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] transition-all min-h-[120px] resize-none"
                          placeholder="Please provide specifics regarding your inquiry..."
                          value={contactMessage}
                          onChange={(e) => setContactMessage(e.target.value)}
                        />
                      </div>
                      <div className="pt-2 flex items-center justify-end">
                        <button
                          type="submit"
                          style={{ backgroundColor: globalBranding?.palette?.lightMain || '#0094c8' }}
                          className="px-6 py-3 text-white rounded-xl font-black uppercase text-xs tracking-widest transition-all hover:scale-[1.02] active:scale-98 cursor-pointer shadow-md border-0 w-full md:w-auto"
                        >
                          Transmit Inquiry
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
    </AnimatePresence>
  );
}
